import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { styled } from '@mui/material/styles';

// material-ui
import { grey } from '@mui/material/colors';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Checkbox from '@mui/material/Checkbox';
import Button from '@mui/material/Button';

// project imports
import Avatar from 'ui-component/extended/Avatar';
import AiLoader from './AiLoader';

// assets
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import GroupIcon from '@mui/icons-material/Group';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import HttpsIcon from '@mui/icons-material/Https';
import CircularProgress from '@mui/material/CircularProgress';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

import SocialMediaIcon from '../Extra/SocialMediaIcon';

import { updateSelectedInfluencers, showPremiumFeatureDialog, updateUser } from 'actions/planner2';
import { checkProfilePermission, unlockProfile } from 'api_handlers/planner2';
import { fetchNewInfluencer } from 'api_handlers/planner2';
import { getDiscoveryInfluencersDetails } from 'api_handlers/planner2';

import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import CloseIcon from '@mui/icons-material/Close';
import { DialogActions } from '@mui/material';
import Chip from '@mui/material/Chip';
import Popover from '@mui/material/Popover';

import SearchReasonCard from '../Extra/SearchReasonCard';

import mixpanel from '../../../../utils/mixpanel';

import FormatListBulletedRoundedIcon from '@mui/icons-material/FormatListBulletedRounded';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2),
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1),
  },
}));


const relevancyChipColor = (score) => {
  const v = parseFloat(score);
  if (Number.isNaN(v)) return { bg: '#eef0f5', fg: '#5b6478', border: '#d1d5db' };
  if (v >= 7) return { bg: '#e6f7ed', fg: '#1b7a45', border: '#86efac' };
  if (v >= 4) return { bg: '#eff6ff', fg: '#1d4ed8', border: '#bfdbfe' };
  return { bg: '#fff4e0', fg: '#9a6a12', border: '#fcd34d' };
};

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    '& .action-container': {
      visibility: 'visible',
      opacity: 1,
    },
  },
}));

// const ActionButton = styled(Button)(({ theme, vibrate }) => ({
//   visibility: 'hidden',
//   opacity: 0,
//   position: 'absolute',
//   right: theme.spacing(2),
//   bottom: '-20px',
//   // transform: 'translateY(-50%)',
//   transition: 'visibility 0s, opacity 0.5s linear',
//   fontSize: '10px',
//   animation: vibrate ? 'vibrate 0.2s ease 5' : 'none', // Vibrate 5 times for 1 second total
//   '@keyframes vibrate': {
//     '0%': { transform: 'translateX(0)' },
//     '25%': { transform: 'translateX(-2px)' },
//     '50%': { transform: 'translateX(2px)' },
//     '75%': { transform: 'translateX(-2px)' },
//     '100%': { transform: 'translateX(0)' },
//   },
// }));

const StyledHeaderColumn = ({ name, Icon }) => {
  return (
    <TableCell sx={{ border: 1, borderColor: grey[200], borderTop: 'none' }}>
      <Grid
        container
        direction={'column'}
        alignItems={'center'}
        justifyContent={'center'}
        spacing={0}
      >
        <Grid item>
          <Icon sx={{ fontSize: 20, color: 'secondary.main' }} />
        </Grid>
        <Grid item sx={{ mb: 1, width: '100%', textAlign: 'center' }}>
          <Typography fontWeight={'bold'} sx={{ color: 'secondary.main' }} fontSize={'0.8rem'}>
            {name}
          </Typography>
        </Grid>
      </Grid>
    </TableCell>
  );
};

const StyledTableCell = ({ children }) => {
  return (
    <TableCell align="center" sx={{ border: 1, borderColor: grey[200] }} fontSize="0.8rem">
      <Typography fontWeight={'bold'} fontSize={'0.8rem'} sx={{ textTransform: 'capitalize' }}>
        {children}
      </Typography>
    </TableCell>
  );
};

const AiUserList = ({ influencers, loading, handleAddToCampaign, detectedLocation }) => {
  const user = useSelector((state) => state.planner2.user);
  const selected = useSelector((state) => state.planner2.selectedInfluencers);

  const dispatch = useDispatch();

  const [profilePermissionMap, setProfilePermissionMap] = React.useState({});
  const [unlockedProfilePermissionMap, setUnlockedProfilePermissionMap] = React.useState({});
  const [loaderMap, setLoaderMap] = React.useState({});

  const [showUnlockedProfileCreditOver, setShowUnlockedProfileCreditOver] = React.useState(false);
  const [showPremiumProfileCreditOver, setShowPremiumProfileCreditOver] = React.useState(false);
  const [showIQProfileNotExists, setShowIQProfileNotExists] = React.useState(false);

  const [vibrate, setVibrate] = useState(false);
  const [searchReasonAnchor, setSearchReasonAnchor] = React.useState(null);
  const [activeSearchReason, setActiveSearchReason] = React.useState('');

  const setSelected = (newSelected) => {
    dispatch(updateSelectedInfluencers(newSelected));
  };

  const onSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = influencers.map((n) => n.id);
      setSelected(new Set(newSelecteds));
      if (process.env.NODE_ENV === 'production') {
        mixpanel.track('discover_select_all_list');
      }
      return;
    }
    setSelected(new Set());
  };

  const handleClick = (event, id) => {
    if (selected.has(id)) {
      const newSelected = new Set(selected);
      newSelected.delete(id);
      setSelected(newSelected);
    } else {
      setSelected(new Set(selected).add(id));
    }
  };

  const fetchDetails = async (getId) => {
    if (getId) {
      setLoaderMap((prevLoaderMap) => {
        console.log('Previous loaderMap:', prevLoaderMap);
        const newLoaderMap = { ...prevLoaderMap, [getId]: !vibrate ? false : true };
        console.log('Updated loaderMap:', newLoaderMap);
        return newLoaderMap;
      });
      console.log('fetch details', loaderMap);

      try {
        const response = await getDiscoveryInfluencersDetails(getId);

        // if (response?.update_status === 'updating') {
        //     setUpdateStatus('updating');
        // }
        // if (response?.update_status === 'updated') {
        //     setUpdateStatus('updated');
        // }
        return response;
      } catch (error) {
        console.error('Error fetching details:', error);
        setLoaderMap((prevLoaderMap) => {
          console.log('Previous loaderMap:', prevLoaderMap);
          const newLoaderMap = { ...prevLoaderMap, [getId]: false };
          console.log('Updated loaderMap:', newLoaderMap);
          return newLoaderMap;
        });
      } finally {
        setLoaderMap((prevLoaderMap) => {
          console.log('Previous loaderMap:', prevLoaderMap);
          const newLoaderMap = { ...prevLoaderMap, [getId]: false };
          console.log('Updated loaderMap:', newLoaderMap);
          return newLoaderMap;
        });
      }
    }
  };

  const clearLoaderForRow = (rowId) => {
    setLoaderMap((prev) => ({ ...prev, [rowId]: false }));
  };

  const handleShowDetails = async (row, open) => {
    setLoaderMap((prev) => ({ ...prev, [row.id]: true }));
    try {
      let response = await fetchDetails(row.id);
      if (response && response.url && !row.url) {
        row.url = response.url;
      }
      if (response?.is_brandconnect === 'yes') {
        openProfileHandler(row, false);
        return;
      }
      if (profilePermissionMap[row.id]) {
        if (!unlockedProfilePermissionMap[row.id]) {
          try {
            const resp = await unlockProfile(row.id, false);
            if (resp?.detail === 'Success') {
              openProfileHandler(row, false);
            } else {
              clearLoaderForRow(row.id);
            }
          } catch (error) {
            clearLoaderForRow(row.id);
            if (error && error.response && error.response.status === 403) {
              showUnlockedProfileCreditOver(true);
            }
          }
        } else {
          openProfileHandler(row, false);
        }
      } else {
        if (open === true) {
          setVibrate((v) => !v);
          setTimeout(() => setVibrate(false), 1000);
          clearLoaderForRow(row.id);
        } else {
          unlockProfileHandler(row);
        }
      }
    } catch (err) {
      clearLoaderForRow(row.id);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(async () => {
    const resp = await checkProfilePermission({
      payload: {
        profile_ids: influencers.map((inf) => inf.id),
      },
    });
    setProfilePermissionMap(resp['profile_id_access_map']['standard-profiles'] || {});
    setUnlockedProfilePermissionMap(resp['profile_id_access_map']['unlocked-profiles'] || {});
  }, [influencers]);

  const openProfileHandler = (row, is_premium) => {
    dispatch(updateUser());
    setProfilePermissionMap((prevData) => ({
      ...prevData,
      [row.id]: true,
    }));
    setUnlockedProfilePermissionMap((prevData) => ({
      ...prevData,
      [row.id]: true,
    }));
    setLoaderMap((prevLoaderMap) => {
      console.log('Previous loaderMap:', prevLoaderMap);
      const newLoaderMap = { ...prevLoaderMap, [row.id]: false };
      console.log('Updated loaderMap:', newLoaderMap);
      return newLoaderMap;
    });
    // 'Insights viewed' is tracked by the profile page itself; type/source ride along in the query.
    window.open(
      `/planner/profile-details/${row.platform}/${
        row.platform === 'instagram' ? `${row.url.split('/')[3]}` : `${row.url.split('/')[4]}`
      }?type=${is_premium ? 'Premium' : 'standard'}&src=ai_discover`,
      '_blank',
    );
  };

  const unlockProfileHandler = async (row) => {
    setLoaderMap((prevLoaderMap) => {
      console.log('Previous loaderMap:', prevLoaderMap);
      const newLoaderMap = { ...prevLoaderMap, [row.id]: true };
      console.log('Updated loaderMap:', newLoaderMap);
      return newLoaderMap;
    });
    try {
      if (!row.audience_data_exist) {
        const { data } = await fetchNewInfluencer({
          payload: {
            link: row.url,
            'with-audience-data': true,
          },
        }, 'AI influencers list › Unlock profile');
        if (data.data && data.data.dump && Object.keys(data.data.dump).length === 0) {
          setShowIQProfileNotExists(true);
          return;
        }
      }

      const resp = await unlockProfile(row.id, true);
      if (resp?.detail === 'Success') {
        openProfileHandler(row, true);
      } else {
        setLoaderMap((prevLoaderMap) => {
          console.log('Previous loaderMap:', prevLoaderMap);
          const newLoaderMap = { ...prevLoaderMap, [row.id]: false };
          console.log('Updated loaderMap:', newLoaderMap);
          return newLoaderMap;
        });
      }
    } catch (error) {
      setLoaderMap((prevLoaderMap) => {
        console.log('Previous loaderMap:', prevLoaderMap);
        const newLoaderMap = { ...prevLoaderMap, [row.id]: false };
        console.log('Updated loaderMap:', newLoaderMap);
        return newLoaderMap;
      });
      if (error && error.response && error.response.status === 403) {
        setShowPremiumProfileCreditOver(true);
      }
    }
  };

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={{ border: 1, borderColor: grey[200], borderTop: 'none' }}>
              <Checkbox
                color="primary"
                indeterminate={selected.size > 0 && selected.size < influencers.length}
                checked={influencers.length > 0 && selected.size === influencers.length}
                onChange={onSelectAllClick}
                inputProps={{
                  'aria-label': 'select all desserts',
                }}
              />
            </TableCell>
            <StyledHeaderColumn name="Profile" Icon={AccountCircleIcon} />
            <StyledHeaderColumn name="Relevance" Icon={AutoAwesomeIcon} />
            <StyledHeaderColumn name="Followers" Icon={GroupIcon} />
            <StyledHeaderColumn name="ER" Icon={GroupAddIcon} />
            <StyledHeaderColumn name="Avg Views" Icon={RemoveRedEyeIcon} />
            <StyledHeaderColumn name="Avg Engagement" Icon={GroupAddIcon} />
            {/* <StyledHeaderColumn name="AI Match" Icon={AutoAwesomeIcon} /> */}
            {/* <StyledHeaderColumn name="Action" Icon={AutoAwesomeIcon} /> */}

            <TableCell>
              <Grid
                container
                direction={'column'}
                alignItems={'center'}
                justifyContent={'center'}
                spacing={0}
              >
                <Grid item>
                  <LocationOnIcon sx={{ fontSize: 20, color: 'secondary.main' }} />
                </Grid>
                <Grid item sx={{ mb: 1 }}>
                  <Typography
                    fontWeight={'bold'}
                    sx={{ color: 'secondary.main' }}
                    fontSize={'0.8rem'}
                  >
                    Location
                  </Typography>
                </Grid>
              </Grid>
            </TableCell>
            <TableCell sx={{ maxWidth: 0 }} align="right" />
          </TableRow>
        </TableHead>
        <TableBody>
        {loading ? (
          <TableRow>
            <TableCell colSpan={8} sx={{ border: 'none' }}>
              <AiLoader />
            </TableCell>
          </TableRow>
        ) : null}

          {!loading && influencers && influencers.length > 0
            ? influencers.map((row, index) => {
                const isItemSelected = selected.has(row.id);
                console.log('influencer',row)
                return (
                  <StyledTableRow hover key={row.id} style={{ position: 'relative' }}>
                    <TableCell
                      sx={{ pl: 2, border: 1, borderColor: grey[200] }}
                      padding="checkbox"
                      onClick={(event) => handleClick(event, row.id)}
                    >
                      <Checkbox
                        color="primary"
                        checked={isItemSelected}
                        inputProps={
                          {
                            // 'aria-labelledby': labelId
                          }
                        }
                      />
                    </TableCell>
                    <TableCell sx={{ border: 1, borderColor: grey[200] }}>
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Avatar
                          alt="User 1"
                          src={row.picture}
                          style={{ cursor: 'pointer' }}
                          onClick={async () => {
                            await handleShowDetails(row, true);
                          }}
                        />
                        <Stack>
                          <Stack
                            direction={'row'}
                            alignItems="center"
                            justifyContent={'center'}
                            spacing={0.1}
                          >
                            <Typography
                              variant="subtitle1"
                              fontWeight={'bold'}
                              fontSize={'0.8rem'}
                              style={{ cursor: 'pointer' }}
                              onClick={async () => {
                                await handleShowDetails(row, true);
                              }}
                            >
                              {row.name
                                ? row.name.length < 20
                                  ? row.name
                                  : `${row.name.slice(0, 20)}...`
                                : ''}
                            </Typography>
                            <Stack>
                              <SocialMediaIcon platform={row.platform} url={row.url} />
                            </Stack>
                          </Stack>
                          <Typography
                            variant="subtitle2"
                            noWrap
                            fontWeight={'bold'}
                            fontSize={'0.8rem'}
                          >
                            {row.language}
                          </Typography>
                        </Stack>
                      </Stack>
                    </TableCell>
                    {/* <TableCell sx={{ border: 1, borderColor: grey[200] }}>
                                    <ExtendedChip
                                        avatar={<CheckCircleIcon sx={{ height: 18 }} />}
                                        label={`SPEED: ${row.speed_score}`}
                                        chipcolor="success"
                                        sx={{
                                            '& .MuiChip-label': {
                                                fontWeight: 800,
                                                fontSize: '0.7rem',
                                                paddingLeft: '6px',
                                                paddingRight: '6px'
                                            },
                                            '& .MuiChip-avatar': {height: 15, marginLeft: 0},
                                            height: 25
                                        }}
                                    />
                                </TableCell> */}
                    <TableCell align="center" sx={{ border: 1, borderColor: grey[200] }}>
                      {row.relevancy_score != null ? (
                        <Chip
                          size="small"
                          label={Number(row.relevancy_score).toFixed(1)}
                          sx={{
                            fontWeight: 700,
                            fontSize: '0.7rem',
                            bgcolor: relevancyChipColor(row.relevancy_score).bg,
                            color: relevancyChipColor(row.relevancy_score).fg,
                            border: `1.5px solid ${relevancyChipColor(row.relevancy_score).border}`,
                            cursor: row.search_reason ? 'pointer' : 'default',
                          }}
                          onClick={row.search_reason ? (e) => {
                            e.stopPropagation();
                            setActiveSearchReason(row.search_reason);
                            setSearchReasonAnchor(e.currentTarget);
                          } : undefined}
                        />
                      ) : (
                        <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: '#1f2433' }}>—</Typography>
                      )}
                    </TableCell>
                    <StyledTableCell>{row.followers}</StyledTableCell>
                    <StyledTableCell>{row.engagement_rate} </StyledTableCell>
                    <StyledTableCell>{row.avg_views} </StyledTableCell>
                    <StyledTableCell>{row.prediction?.enagaged_users_display}</StyledTableCell>
                    {/* <StyledTableCell>
                      <ExtendedChip
                        label={90}
                        sx={{ color: '#000', background: '#f9b715' }}
                      ></ExtendedChip>
                    </StyledTableCell> */}
                    <TableCell align="center" sx={{ maxWidth: 200, border: 1, borderColor: grey[200] }}>
                      <Typography fontWeight={'bold'} fontSize={'0.8rem'} sx={{ textTransform: 'capitalize' }}>
                        {(() => {
                          const city = row.city && row.city.toLowerCase() !== 'other' && row.city.toLowerCase() !== (detectedLocation || '').toLowerCase() ? row.city : null;
                          if (city && detectedLocation) return `${city}, ${detectedLocation}`;
                          if (city) return city;
                          if (detectedLocation) return detectedLocation;
                          return '-';
                        })()}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ maxHeight: '100%', position: 'relative' }} align="right">
                      <Grid
                        container
                        spacing={1}
                        width={'50%'}
                        sx={{
                          visibility: 'hidden',
                          opacity: 0,
                          position: 'absolute',
                          right: '1%',
                          top: '-0.1px',
                          marginTop: '1px',
                          width: '600px',
                          paddingRight: '10px',
                        }}
                        className="action-container"
                        height={'85%'}
                        justifyContent={'flex-end'}
                        alignItems={'center'}
                      >
                        <Grid item>
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={(event) => {
                              if (process.env.NODE_ENV === 'production') {
                                mixpanel.track('discover_add_to_list', {
                                  type: 'collab_list',
                                });
                              }
                              if (user.premium_user){
                              handleAddToCampaign([row.id], 'Planner');
                              }
                              else dispatch(showPremiumFeatureDialog(true));
                            }}
                            startIcon={
                              <FormatListBulletedRoundedIcon
                                style={{ width: '10px', height: '15px' }}
                              />
                            }
                            sx={{
                              fontSize: 10,
                              width: '130px',
                            }}
                          >
                            Add to List
                          </Button>
                        </Grid>
                        <Grid item>
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={(event) => {
                              event.stopPropagation();
                              if (user?.premium_user) {
                                handleAddToCampaign([row.id], 'AddToCampaign');
                              } else {
                                dispatch(showPremiumFeatureDialog(true));
                              }
                            }}
                            sx={{
                              fontSize: 10,
                              width: '130px',
                            }}
                            startIcon={
                              <WorkspacePremiumIcon style={{ width: '10px', height: '15px' }} />
                            }
                          >
                            Add to Campaign
                          </Button>
                        </Grid>
                        <Grid item>
                          <Tooltip
                            title={
                              !profilePermissionMap[row.id]
                                ? 'Unlock this profile for 1 premium credit.'
                                : !user?.premium_user &&
                                  profilePermissionMap[row.id] &&
                                  !unlockedProfilePermissionMap[row.id]
                                ? 'Unlock this profile  for 1 standard credit.'
                                : ''
                            }
                          >
                            <Button
                              variant="contained"
                              color="primary"
                              vibrate={vibrate}
                              onClick={() => {
                                if (user?.credit_balance === 0 && !profilePermissionMap[row.id]) {
                                  setShowPremiumProfileCreditOver(true);
                                } else if (
                                  !user?.premium_user &&
                                  user?.fetched_credit_balance === 0 &&
                                  profilePermissionMap[row.id] &&
                                  !unlockedProfilePermissionMap[row.id]
                                ) {
                                  setShowUnlockedProfileCreditOver(true);
                                } else {
                                  handleShowDetails(row, false);
                                }
                              }}
                              style={{
                                color: loaderMap[row.id] ? 'black' : 'black',
                                fontSize: 10,
                                width: '130px',
                              }}
                              sx={{
                                ...(loaderMap[row.id] && { opacity: 0.85, cursor: 'wait' }),
                                animation: vibrate ? 'vibrate 0.2s ease 5' : 'none',
                                '@keyframes vibrate': {
                                  '0%': { transform: 'translateX(0)' },
                                  '25%': { transform: 'translateX(-2px)' },
                                  '50%': { transform: 'translateX(2px)' },
                                  '75%': { transform: 'translateX(-2px)' },
                                  '100%': { transform: 'translateX(0)' },
                                },
                              }}
                              startIcon={
                                loaderMap[row.id] ? (
                                  <CircularProgress size={16} sx={{ color: 'black' }} />
                                ) : profilePermissionMap[row.id] ? (
                                  <RemoveRedEyeIcon style={{ width: '10px', height: '15px' }} />
                                ) : (
                                  <HttpsIcon style={{ width: '10px', height: '15px' }} />
                                )
                              }
                              disabled={loaderMap[row.id]}
                            >
                              {loaderMap[row.id] ? 'Loading...' : 'Show Details'}
                            </Button>
                          </Tooltip>
                        </Grid>
                      </Grid>
                    </TableCell>
                  </StyledTableRow>
                );
              })
            : null}
        </TableBody>
      </Table>

      <Popover
        open={Boolean(searchReasonAnchor)}
        anchorEl={searchReasonAnchor}
        onClose={() => setSearchReasonAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { maxWidth: 360, p: 1.75, borderRadius: '12px' } }}
      >
        <SearchReasonCard reason={activeSearchReason} />
      </Popover>

      {showUnlockedProfileCreditOver ? (
        <React.Fragment>
          <BootstrapDialog
            onClose={() => {
              setShowUnlockedProfileCreditOver(false);
            }}
            aria-labelledby="customized-dialog-title"
            open={true}
            fullWidth
          >
            <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title"></DialogTitle>
            <IconButton
              aria-label="close"
              onClick={() => {
                setShowUnlockedProfileCreditOver(false);
              }}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
              }}
            >
              <CloseIcon />
            </IconButton>
            <DialogContent dividers style={{ textAlign: 'center' }}>
              You’ve reached the limit of unlocking standard profiles. Use your remaining credits to
              unlock premium profiles or upgrade for unlimited access.
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => {
                  setShowUnlockedProfileCreditOver(false);
                  dispatch(showPremiumFeatureDialog(true));
                }}
              >
                Upgrade
              </Button>
            </DialogActions>
          </BootstrapDialog>
        </React.Fragment>
      ) : null}
      {showPremiumProfileCreditOver ? (
        <React.Fragment>
          <BootstrapDialog
            onClose={() => {
              setShowPremiumProfileCreditOver(false);
            }}
            aria-labelledby="customized-dialog-title"
            open={true}
            fullWidth
          >
            <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title"></DialogTitle>
            <IconButton
              aria-label="close"
              onClick={() => {
                setShowPremiumProfileCreditOver(false);
              }}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
              }}
            >
              <CloseIcon />
            </IconButton>
            <DialogContent dividers style={{ textAlign: 'center' }}>
              You’ve used all your premium credits. Upgrade to unlock more premium profiles!
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => {
                  setShowPremiumProfileCreditOver(false);
                  dispatch(showPremiumFeatureDialog(true));
                }}
              >
                Upgrade
              </Button>
            </DialogActions>
          </BootstrapDialog>
        </React.Fragment>
      ) : null}
      {showIQProfileNotExists ? (
        <React.Fragment>
          <BootstrapDialog
            onClose={() => {
              setShowIQProfileNotExists(false);
            }}
            aria-labelledby="customized-dialog-title"
            open={true}
            fullWidth
          >
            <DialogTitle
              sx={{ m: 0, p: 2, textAlign: 'center', fontWeight: 'bold' }}
              id="customized-dialog-title"
            >
              We are fetching the data for this profile.
            </DialogTitle>
            <IconButton
              aria-label="close"
              onClick={() => {
                setShowIQProfileNotExists(false);
              }}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
              }}
            >
              <CloseIcon />
            </IconButton>
            <DialogContent dividers style={{ textAlign: 'center' }}>
              <div style={{ marginBottom: '15px', display: 'flex', alignItems: 'center' }}>
                <CircularProgress size={30} sx={{ marginRight: '10px' }} />
                Please wait while we are fetching the latest data for this profile, you can come
                back and check later.
              </div>
            </DialogContent>
          </BootstrapDialog>
        </React.Fragment>
      ) : null}
    </TableContainer>
  );
};

export default AiUserList;
